import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@platform/ui';
import { formatKstDate } from '@platform/datetime';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { useReactivationPreview } from '../api/use-subscription.query';
import { useBillingActions } from '../hooks/useBillingActions';

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

/** 결제할 수 없는 상태의 안내 문구. 버튼을 눌러야 알게 되면 그건 확인 절차가 아니다. */
const BLOCKED_DESC: Record<string, string> = {
  ALREADY_ACTIVE: '이미 이용 중인 구독입니다.',
  NOT_RESUMABLE: '일시정지·연체 상태의 구독만 재개할 수 있습니다.',
  NO_BILLING_KEY: '등록된 결제수단이 없습니다. 카드를 먼저 등록해주세요.',
};

/**
 * 일시정지(PAUSED) 구독 재활성화 화면.
 * (연체 안내 메일/알림톡 CTA 또는 구독·라이선스 UI 에서 진입)
 *
 * 재개는 **즉시 전액 청구**다. 그래서 금액을 먼저 보여주고 버튼 라벨에도 넣는다
 * (전상법 §14 청약 내용 확인). 금액은 서버 프리뷰가 내려준 값을 그대로 쓴다 —
 * 화면에서 다시 계산하면 실제 청구액과 언젠가 갈라진다.
 *
 * 1) "{금액} 결제하고 재개" → 기존 billingKey 로 즉시 청구(fast-path).
 * 2) 실패하거나 사용자가 원하면 → "다른 카드로 재등록"(카드 재등록 후 재활성화).
 */
export function ReactivateView() {
  const { id } = useParams<{ id: string }>();
  const { busy, reactivateExistingCard, reactivateWithNewCard } =
    useBillingActions();
  const [state, setState] = useState<'idle' | 'failed' | 'done'>('idle');
  const [failMessage, setFailMessage] = useState('');
  const { data: preview, isPending: previewLoading } = useReactivationPreview(
    id ?? null,
  );

  if (!id) {
    return (
      <Centered>
        <h1 className="text-lg font-bold text-red-600">잘못된 접근</h1>
        <p className="mt-2 text-sm text-gray-500">구독 정보가 없습니다.</p>
        <Link to={PATHS.MYPAGE} className="mt-4 inline-block">
          <Button variant="secondary" size="sm">
            마이페이지
          </Button>
        </Link>
      </Centered>
    );
  }

  if (state === 'done') {
    return (
      <Centered>
        <h1 className="text-xl font-bold text-green-600">
          구독이 재개되었습니다
        </h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-neutral-400">
          결제가 완료되어 서비스 이용이 복구되었습니다.
        </p>
        <div className="mt-5 flex justify-center gap-2">
          <Link to={PATHS.MYPAGE}>
            <Button variant="primary" size="sm">
              마이페이지로
            </Button>
          </Link>
          <Link to={PATHS.BUSINESS}>
            <Button variant="secondary" size="sm">
              기업 콘솔로
            </Button>
          </Link>
        </div>
      </Centered>
    );
  }

  const blockedDesc =
    preview && !preview.chargeable ? BLOCKED_DESC[preview.reason] : null;

  return (
    <Centered>
      <h1 className="text-xl font-bold text-gray-900 dark:text-neutral-100">
        구독 재개
      </h1>
      <p className="mt-2 text-sm text-gray-500 dark:text-neutral-400">
        일시정지된 구독을 다시 시작합니다. 결제가 완료되면 즉시 이용이
        복구됩니다.
      </p>

      {/* 청구 예정 금액 — 결제 버튼보다 위에, 버튼보다 크게. */}
      {preview && (
        <div className="mt-5 rounded-lg bg-gray-50 p-4 text-left dark:bg-white/5">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-500 dark:text-neutral-400">
              지금 결제할 금액
            </span>
            <span className="text-lg font-bold text-gray-900 dark:text-neutral-100">
              {won(preview.amount)}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
            {preview.planName}
            {preview.seatCount > 1 && ` · ${preview.seatCount}석`} · 다음 결제일{' '}
            {formatKstDate(preview.nextPeriodEnd)}
          </p>
        </div>
      )}

      {blockedDesc && (
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {blockedDesc}
        </p>
      )}

      {state === 'failed' && (
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {failMessage}
          <br />
          다른 카드로 다시 시도할 수 있어요.
        </p>
      )}

      <div className="mt-5 flex flex-col items-center gap-2">
        {state !== 'failed' && (
          <Button
            variant="primary"
            // 금액을 못 받은 상태로는 결제 버튼을 열지 않는다 — 그러면 다시
            // "얼마인지 모르고 누르는" 버튼이 된다.
            disabled={busy || previewLoading || !preview?.chargeable}
            onClick={async () => {
              const result = await reactivateExistingCard(id);
              if (!result.ok) setFailMessage(result.message);
              setState(result.ok ? 'done' : 'failed');
            }}
          >
            {busy
              ? '결제 중…'
              : preview
                ? `${won(preview.amount)} 결제하고 재개`
                : '금액 확인 중…'}
          </Button>
        )}
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => void reactivateWithNewCard(id)}
        >
          다른 카드로 재등록
        </Button>
      </div>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md px-4 py-20 text-center">{children}</div>
  );
}
